import { useEffect, useState } from "react";
import { toast } from "sonner";
import { errorMessage } from "@/lib/error-message";
import { QueryError } from "@/components/Common/QueryError";
import { Save, MapPin, Share2, Clock, Copyright } from "lucide-react";
import { useFooter, useUpdateFooter } from "./queries";
import type { FooterConfig } from "../../api/endpoints";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const EMPTY: FooterConfig = {
  address: { tr: "", en: "" },
  phone: "",
  email: "",
  social: { facebook: "", instagram: "", linkedin: "" },
  workingHours: { tr: "", en: "" },
  copyright: { tr: "", en: "" },
  holdingLabel: "",
  holdingUrl: "",
};

export default function FooterPage() {
  const { data, isLoading, isError, error, refetch } = useFooter();
  const update = useUpdateFooter();
  const [form, setForm] = useState<FooterConfig>(EMPTY);

  useEffect(() => {
    if (data) setForm(data);
  }, [data]);

  async function handleSave() {
    try {
      await update.mutateAsync(form);
      toast.success("Footer kaydedildi.");
    } catch (e) {
      toast.error(errorMessage(e, "Kaydedilemedi. Lütfen tekrar deneyin."));
    }
  }

  const set = <K extends keyof FooterConfig>(key: K, value: FooterConfig[K]) =>
    setForm((s) => ({ ...s, [key]: value }));

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Footer</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Sitenin alt bilgisindeki iletişim, sosyal medya, çalışma saatleri ve telif metnini
          yönetin. Boş bırakılan alanlar varsayılan değerle doldurulur.
        </p>
      </div>

      {isLoading ? (
        <>
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-40 w-full" />
        </>
      ) : isError && !data ? (
        // Veri gelmeden boş formu kaydetmek mevcut footer'ı silerdi — formu gösterme.
        <QueryError error={error} onRetry={() => void refetch()} />
      ) : (
        <>
          {/* İletişim */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <MapPin className="w-5 h-5" /> İletişim
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1.5">
                <Label>Adres (TR)</Label>
                <Textarea
                  value={form.address.tr}
                  onChange={(e) => set("address", { ...form.address, tr: e.target.value })}
                  rows={2}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Adres (EN)</Label>
                <Textarea
                  value={form.address.en}
                  onChange={(e) => set("address", { ...form.address, en: e.target.value })}
                  rows={2}
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Telefon</Label>
                  <Input
                    value={form.phone}
                    onChange={(e) => set("phone", e.target.value)}
                    placeholder="+90 (216) 523 35 70"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>E-posta</Label>
                  <Input
                    value={form.email}
                    onChange={(e) => set("email", e.target.value)}
                    placeholder="bilgi@endeksbilisim.com"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Sosyal Medya */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Share2 className="w-5 h-5" /> Sosyal Medya
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {(["facebook", "instagram", "linkedin"] as const).map((k) => (
                <div key={k} className="space-y-1.5">
                  <Label className="capitalize">{k}</Label>
                  <Input
                    value={form.social[k]}
                    onChange={(e) => set("social", { ...form.social, [k]: e.target.value })}
                    placeholder={`https://... (boş bırakılırsa ${k} gizlenir)`}
                  />
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Çalışma Saatleri */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Clock className="w-5 h-5" /> Çalışma Saatleri
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>TR</Label>
                <Input
                  value={form.workingHours.tr}
                  onChange={(e) =>
                    set("workingHours", { ...form.workingHours, tr: e.target.value })
                  }
                  placeholder="Pzt - Cum: 09:00 - 18:30"
                />
              </div>
              <div className="space-y-1.5">
                <Label>EN</Label>
                <Input
                  value={form.workingHours.en}
                  onChange={(e) =>
                    set("workingHours", { ...form.workingHours, en: e.target.value })
                  }
                  placeholder="Mon - Fri: 09:00 - 18:30"
                />
              </div>
            </CardContent>
          </Card>

          {/* Telif */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Copyright className="w-5 h-5" /> Telif Metni
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1.5">
                <Label>Telif (TR)</Label>
                <Textarea
                  value={form.copyright.tr}
                  onChange={(e) => set("copyright", { ...form.copyright, tr: e.target.value })}
                  rows={2}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Telif (EN)</Label>
                <Textarea
                  value={form.copyright.en}
                  onChange={(e) => set("copyright", { ...form.copyright, en: e.target.value })}
                  rows={2}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Metinde <code>{"{link}"}</code> yazan yer, aşağıdaki bağlantı olarak gösterilir.
                Örn: <em>“..., bir {"{link}"} iştirakidir.”</em>
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Bağlantı Metni</Label>
                  <Input
                    value={form.holdingLabel}
                    onChange={(e) => set("holdingLabel", e.target.value)}
                    placeholder="Karabiber Holding"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Bağlantı URL</Label>
                  <Input
                    value={form.holdingUrl}
                    onChange={(e) => set("holdingUrl", e.target.value)}
                    placeholder="https://www.karabiberholding.com"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Button onClick={handleSave} disabled={update.isPending} className="gap-2">
            <Save className="w-4 h-4" />
            {update.isPending ? "Kaydediliyor..." : "Kaydet"}
          </Button>
        </>
      )}
    </div>
  );
}
